import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import type { FeeInfo } from "../../../../../view-models/feeInfo";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import {
  getMapiQuoteTransactions,
  mapiTransactionToFeeInfoLike,
} from "../../../../../helper/mapiQuoteResponseHelpers";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import RightArrowIcon from "../../../../../../../../assets/images/chevron-right.svg";
import BankIcon from "../../../../../../../../assets/images/BankIcon.svg";
import LocationIcon from "../../../../../../../../assets/images/LocationCash.svg";
import WalletIcon from "../../../../../../../../assets/images/WalletIcon.svg";
import HomeIcon from "../../../../../../../../assets/images/HomeIcon.svg";
import HowTheyReceiveModal from "../../../common/HowTheyReceiveModal";

type MapiHowTheyReceiveStepProps = {
  onCountrySelected?: () => void;
  onValidationChange?: (isValid: boolean) => void;
  exchangeRate?: number;
  receiveCurrency?: string;
  testID?: string | undefined;
};

export default function MapiHowTheyReceiveStep({
  onCountrySelected,
  onValidationChange,
  exchangeRate = 1,
  receiveCurrency = "",
  testID,
}: MapiHowTheyReceiveStepProps) {
  const { t, i18n } = useTranslation(["translation", "delivery"]);
  const [isWalletVisible, setIsWalletVisible] = useState(false);
  const [isCashPickUp, setIsCashPickUp] = useState(false);
  const [modalTitle, setModalTitle] = useState("Mobile Wallet");
  const [modalSubTitle, setModalSubTitle] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<any[]>([]);

  const setPendingSendOption = useSessionStore(
    (s: SessionState) => (s as any).setPendingSendOption
  );
  const setSelectedDeliveryOptionStore = useSessionStore(
    (s: SessionState) => (s as any).setSelectedDeliveryOptionStore
  );
  const pendingSendOption = useSessionStore(
    (s: SessionState) => (s as any).pendingSendOption
  );
  const selectedSendOption = useSessionStore(
    (s: SessionState) => (s as any).selectedSendOption
  );
  const mapiQuoteData = useSessionStore(
    (s: SessionState) => (s as any).mapiQuoteResponse
  );
  const deliveryOptions: any[] = useMemo(
    () => getMapiQuoteTransactions(mapiQuoteData),
    [mapiQuoteData]
  );

  const colors = ["#FF6B6B", "#4ECDC4", "#FFD93D", "#6A4C93", "#1A535C"];
  const randomColor = (index: number) => colors[index % colors.length];

  const feeInfos = useMemo(() => {
    return (deliveryOptions || []).map((row: any) => {
      const raw = row?.raw ?? row;
      return mapiTransactionToFeeInfoLike(raw, exchangeRate, receiveCurrency);
    });
  }, [deliveryOptions, exchangeRate, receiveCurrency]);

  const isValid = selected != null;

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  const scheduleAdvanceToNextStep = () => {
    onValidationChange?.(true);
    setTimeout(() => {
      onCountrySelected?.();
    }, 0);
  };

  const getOptionKey = (fee: FeeInfo): string => {
    const m = fee as any;
    return JSON.stringify({
      sendCurrency: fee?.SendAmounts?.SendCurrency || null,
      exchangeRate: FeeLookupHelper.getExchangeRate(fee) || null,
      receiveCurrency: FeeLookupHelper.getReceiveCurrency(fee) || null,
      deliveryOptDisplayName: fee.DeliveryOptDisplayName || null,
      receiveAgentId:
        (fee as any).ReceiveAgentId ||
        m?.__mapiTx?.transactionId ||
        null,
    });
  };

  const getRowListKey = (item: any): string => {
    if (item?.type === "grouped" && item?.agents?.[0]?.Raw) {
      return getOptionKey(item.agents[0].Raw);
    }
    return getOptionKey(item);
  };

  const formatExchangeRate = (fee: FeeInfo): string => {
    const sendCurrency = fee?.SendAmounts?.SendCurrency || "USD";
    const rate = FeeLookupHelper.getExchangeRate(fee);
    const receiveCurrencyRow = FeeLookupHelper.getReceiveCurrency(fee);
    const rateNum = Number(rate);
    const rateFixed = isFinite(rateNum) ? rateNum.toFixed(4) : String(rate);
    return `1 ${sendCurrency} = ${rateFixed} ${receiveCurrencyRow}`;
  };

  const formatFee = (fee: FeeInfo): string => {
    const amount = Number(fee?.SendAmounts?.TotalSendFees || 0);
    const currency = fee?.SendAmounts?.SendCurrency || "USD";
    return `${amount.toFixed(2)} ${currency}`;
  };

  const groupedData = useMemo(() => {
    const cashPickupGroup: any[] = [];
    const walletGroup: any[] = [];
    const accountDepositGroup: any[] = [];
    const singles: any[] = [];

    feeInfos.forEach((item: any) => {
      const option = item?.DeliveryOption;

      if (option === "WILL_CALL" || option === "WILLCALL_TO") {
        cashPickupGroup.push(item);
      } else if (option === "DIRECT_TO_ACCT") {
        walletGroup.push(item);
      } else if (option === "BANK_DEPOSIT") {
        accountDepositGroup.push(item);
      } else {
        singles.push({ ...item, type: "single" });
      }
    });

    const makeGroup = (groupArray: any[]) => {
      if (groupArray.length <= 1) {
        return { ...groupArray[0], type: "single" };
      }

      const first = groupArray[0];

      return {
        type: "grouped",
        DeliveryOptDisplayName: first.DeliveryOptDisplayName,
        DeliveryOption: first.DeliveryOption,
        DestinationCountry: first.DestinationCountry,
        Fee: first.EstimatedReceiveAmount,
        ExchangeRate: first.ValidExchangeRate,
        agents: groupArray.map((g: any) => ({
          ReceiveAgentAbbreviation: g.ReceiveAgentAbbreviation,
          ReceiveAgentName: g.ReceiveAgentName,
          ReceiveAgentId: g.ReceiveAgentId,
          AgentLogos: g.AgentLogos,
          Fee: g.EstimatedReceiveAmount,
          ExchangeRate: g.ValidExchangeRate,
          Raw: g,
        })),
      };
    };

    const result: any[] = [];

    if (cashPickupGroup?.length) result.push(makeGroup(cashPickupGroup));
    if (walletGroup?.length) result.push(makeGroup(walletGroup));
    if (accountDepositGroup?.length)
      result.push(makeGroup(accountDepositGroup));

    return [...result, ...singles];
  }, [feeInfos]);

  const modalAutoOpenedThisMountRef = useRef(false);
  useEffect(() => {
    const savedForHighlight = selectedSendOption ?? pendingSendOption;
    if (!savedForHighlight || feeInfos.length === 0) return;

    const savedFee = mapiTransactionToFeeInfoLike(
      savedForHighlight,
      exchangeRate,
      receiveCurrency
    );
    const key = getOptionKey(savedFee);
    setSelected(key);

    if (modalAutoOpenedThisMountRef.current) return;
    if (!selectedSendOption) return;

    const committedFee = mapiTransactionToFeeInfoLike(
      selectedSendOption,
      exchangeRate,
      receiveCurrency
    );
    const committedKey = getOptionKey(committedFee);
    if (committedKey !== key) return;

    for (const row of groupedData) {
      if (row?.type !== "grouped" || !row.agents?.length) continue;
      const agentMatch = row.agents.find(
        (a: any) => getOptionKey(a.Raw) === key
      );
      if (!agentMatch) continue;

      modalAutoOpenedThisMountRef.current = true;
      setSelectedWallet(row.agents);
      const name = row.DeliveryOptDisplayName?.toLowerCase?.() ?? "";
      const isCash =
        name.includes("cash pickup") ||
        row.DeliveryOption === "WILL_CALL" ||
        row.DeliveryOption === "WILLCALL_TO";
      const isWallet =
        name.includes("send to wallet") ||
        row.DeliveryOption === "DIRECT_TO_ACCT";
      const isAccountDeposit =
        row.DeliveryOption === "BANK_DEPOSIT" ||
        name.includes("account deposit");
      if (isCash) {
        setIsCashPickUp(true);
        setModalTitle(t("delivery:CASH_PICKUP_TITLE"));
        setModalSubTitle(t("delivery:CASH_PICKUP_SUBTITLE"));
      } else if (isAccountDeposit) {
        setIsCashPickUp(false);
        setModalTitle(t("delivery:BANK_DEPOSIT"));
        setModalSubTitle("");
      } else {
        setIsCashPickUp(false);
        setModalTitle(t("delivery:MOBILE_WALLET_TITLE"));
        setModalSubTitle("");
      }
      setIsWalletVisible(true);
      break;
    }
  }, [
    feeInfos,
    groupedData,
    pendingSendOption,
    selectedSendOption,
    exchangeRate,
    receiveCurrency,
    t,
  ]);

  const filteredAgents = useMemo(() => {
    const q = search.toLowerCase();
    return selectedWallet.filter((item: any) => {
      const name =
        item?.ReceiveAgentName ||
        item?.Raw?.ReceiveAgentName ||
        item?.Raw?.DeliveryOptDisplayName ||
        "";
      return name.toLowerCase().includes(q);
    });
  }, [selectedWallet, search]);

  const persistSelection = (mapiTx: any) => {
    try {
      setPendingSendOption(mapiTx);
    } catch {
      /* ignore */
    }
    try {
      setSelectedDeliveryOptionStore?.({
        DeliveryOption:
          mapiTx?.serviceOptionCode ?? mapiTx?.DeliveryOption ?? null,
        option: mapiTx,
      });
    } catch {
      /* ignore */
    }
  };

  const onSelectWalletOption = (item: any) => {
    const raw = item?.Raw;
    const mapiTx = raw?.__mapiTx ?? raw;
    const key = getOptionKey(raw);
    setSelected(key);
    persistSelection(mapiTx);
    setTimeout(() => {
      setIsWalletVisible(false);
      setIsCashPickUp(false);
    }, 200);
    scheduleAdvanceToNextStep();
  };

  const oncloseModal = () => {
    setIsWalletVisible(false);
    setIsCashPickUp(false);
  };

  const getTranslatedDeliveryName = (item: any): string => {
    const key =
      item?.DeliveryOption ||
      item?.DeliveryOptId ||
      item?.DeliveryOptDisplayName;
    return t(`delivery:${key}`, {
      defaultValue:
        item?.DeliveryOptDisplayName ||
        item?.ReceiveAgentName ||
        t("RECEIVE_METHOD_DELIVERY_OPTION"),
    });
  };

  const getDeliveryTranslationKey = (item: any) => {
    return (
      item?.DeliveryOption ||
      item?.DeliveryOptId ||
      item?.DeliveryOptDisplayName
    );
  };

  const renderHowTheyReceiveOption = (item: any) => {
    const key = getRowListKey(item);
    const isGroupRowSelected =
      item?.type === "grouped" &&
      !!selected &&
      !!item?.agents?.some((a: any) => getOptionKey(a.Raw) === selected);
    const isSelected = isGroupRowSelected || selected === key;
    const isSpecial = item?.type === "grouped" ? true : false;

    if (isSpecial) {
      return (
        <TouchableOpacity
          testID={`mapiDeliveryOption-${key}`}
          style={[styles.specialRow, isSelected && styles.specialRowSelected]}
          onPress={() => {
            const name = item?.DeliveryOptDisplayName?.toLowerCase?.() ?? "";
            const isCash =
              name.includes("cash pickup") ||
              item?.DeliveryOption === "WILL_CALL" ||
              item?.DeliveryOption === "WILLCALL_TO";
            const isWallet =
              name.includes("send to wallet") ||
              item?.DeliveryOption === "DIRECT_TO_ACCT";
            const isAccountDeposit =
              item?.DeliveryOption === "BANK_DEPOSIT" ||
              name.includes("account deposit");

            if (isCash || isWallet || isAccountDeposit) {
              setIsWalletVisible(true);
              if (isCash) {
                setIsCashPickUp(true);
                setModalTitle(t("delivery:CASH_PICKUP_TITLE"));
                setModalSubTitle(t("delivery:CASH_PICKUP_SUBTITLE"));
              } else if (isAccountDeposit) {
                setIsCashPickUp(false);
                setModalTitle(t("delivery:BANK_DEPOSIT"));
                setModalSubTitle("");
              } else {
                setIsCashPickUp(false);
                setModalTitle(t("delivery:MOBILE_WALLET_TITLE"));
                setModalSubTitle("");
              }
              setSelectedWallet(item?.agents);
            }
          }}
        >
          {item?.DeliveryOption === "BANK_DEPOSIT" ? (
            <BankIcon width={24} height={24} />
          ) : item?.DeliveryOption === "DIRECT_TO_ACCT" ? (
            <WalletIcon width={24} height={24} />
          ) : (
            <LocationIcon width={24} height={24} />
          )}
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={styles.methodTitle}>
              {getTranslatedDeliveryName(item)}
            </Text>
            <View style={styles.providersRow}>
              {item?.agents?.slice(0, 3).map((_: any, index: number) => (
                <View
                  key={index}
                  style={[
                    styles.providerCircle,
                    { backgroundColor: randomColor(index) },
                  ]}
                />
              ))}
              {item?.agents?.length > 3 && (
                <View style={styles.moreCircle}>
                  <Text style={{ color: "#fff", fontSize: 12 }}>
                    +{item?.agents?.length - 3}
                  </Text>
                </View>
              )}
            </View>
          </View>
          <RightArrowIcon width={24} height={24} />
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        testID={`mapiDeliveryOption-${key}`}
        style={styles.methodRow}
        onPress={() => {
          setSelected(key);
          const mapiTx = item?.__mapiTx ?? item;
          persistSelection(mapiTx);
          scheduleAdvanceToNextStep();
        }}
      >
        {item?.DeliveryOption === "BANK_DEPOSIT" ? (
          <BankIcon width={24} height={24} />
        ) : item?.DeliveryOption === "DIRECT_TO_ACCT" ? (
          <WalletIcon width={24} height={24} />
        ) : item?.DeliveryOption === "WILL_CALL" ||
          item?.DeliveryOption === "WILLCALL_TO" ? (
          <LocationIcon width={24} height={24} />
        ) : (
          <HomeIcon width={24} height={24} />
        )}
        <View style={{ flex: 1, marginLeft: 12, marginRight: 10 }}>
          <Text style={styles.methodTitle}>
            {t(`delivery:${getDeliveryTranslationKey(item)}`, {
              defaultValue:
                item?.DeliveryOptDisplayName ||
                item?.ReceiveAgentName ||
                t("RECEIVE_METHOD_DELIVERY_OPTION"),
            })}
          </Text>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text style={styles.methodDetails}>{formatExchangeRate(item)}</Text>
            <View style={styles.dotStyle} />
            <Text style={styles.methodDetails}>{formatFee(item)}</Text>
            {!!item.ReceiveAgentAbbreviation && (
              <>
                <View style={styles.dotStyle} />
                <Text style={styles.methodDetails}>
                  {item.ReceiveAgentAbbreviation}
                </Text>
              </>
            )}
          </View>
        </View>
        <View style={styles.radioButton}>
          {isSelected && <RadioSelectedIcon width={20} height={20} />}
        </View>
      </TouchableOpacity>
    );
  };

  const listFooter = (
    <>
      {i18n.language === "en" ? (
        <Text style={styles.methodDetails}>
          <Text style={{ fontWeight: "700" }}>Transfer speed </Text>
          depends on the delivery method, transaction review, and system
          availability of MoneyGram and its partners.
        </Text>
      ) : (
        <Text style={styles.methodDetails}>{t("HOW_THEY_RECEIVE_INFO")}</Text>
      )}
    </>
  );

  return (
    <View style={styles.container} testID={testID}>
      {/*
        This step renders inside MapiMainSendCashScreen's outer ScrollView.
        Do not use position:fixed for the title or a separately scrolling FlatList on web:
        the fixed title stays viewport-relative while rows scroll with the parent, which
        clips/overlaps the first options above "How they receive".
      */}
      <View style={styles.titleSection}>
        <Text style={styles.title}>{t("RECEIVE_METHOD_TITLE")}</Text>
      </View>
      <FlatList
        testID="mapiDeliveryOptionsList"
        data={groupedData}
        keyExtractor={(item) => getRowListKey(item)}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        scrollEnabled={false}
        renderItem={({ item }) => (
          <View>{renderHowTheyReceiveOption(item)}</View>
        )}
        ListFooterComponent={listFooter}
        ListEmptyComponent={
          <Text style={styles.emptyText}>{t("RECEIVE_METHOD_NO_OPTIONS")}</Text>
        }
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      />
      <HowTheyReceiveModal
        visible={isWalletVisible}
        isCashPickUp={isCashPickUp}
        onClose={oncloseModal}
        title={modalTitle}
        subTitle={modalSubTitle}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredAgents}
        howTheyReceiveModalTestID="mapiHowTheyReceiveModal"
        onSelectWalletOption={onSelectWalletOption}
        selected={selected}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  titleSection: {
    flexShrink: 0,
    paddingBottom: 8,
  },
  list: {
    alignSelf: "stretch",
  },
  listContent: {
    paddingBottom: 32,
  },
  emptyText: {
    color: "#6B7280",
    marginTop: 8,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  methodRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 14,
  },
  dotStyle: {
    height: 3,
    width: 3,
    borderRadius: 50,
    backgroundColor: Colors.titleTextColor,
    marginHorizontal: 4,
  },
  methodTitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 20,
    color: Colors.titleTextColor,
    marginBottom: 4,
    flexShrink: 1,
  },
  methodDetails: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    lineHeight: 16,
    color: Colors.titleTextColor,
  },
  providersRow: { flexDirection: "row", marginTop: 6 },
  providerCircle: { height: 24, width: 24, borderRadius: 50 },
  moreCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#6B7280",
    alignItems: "center",
    justifyContent: "center",
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#D1D1D1",
  },
  specialRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
  },
  specialRowSelected: {
    borderColor: Colors.black,
    backgroundColor: "#F5F5F5",
  },
});
